import React from "react";

import Container from "@mui/system/Container";
import GearClosetDataTable from "./GearClosetDataTable";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import GearBinContainer from "./GearBinContainer";
import {
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
} from "@mui/material";
import { debounce } from "lodash";
import {
  ChevronDown,
  ChevronRight,
  Eraser,
  Plus,
  Undo2,
  X,
} from "lucide-react";
import {
  getAccent,
  getMutedText,
  HEADING_FONT,
} from "../constants/colors";
import {
  createNewBin,
  deleteUserBin,
  getBins,
  updateUserBin,
  getUserPackData,
} from "../utils";

const TOOLBAR_BUTTON_SX = {
  borderRadius: "9px",
  fontFamily: HEADING_FONT,
  fontWeight: 600,
  textTransform: "none",
  whiteSpace: "nowrap",
};

const GearCloset = ({
  setGearClosetIsOpen,
  handleSetAuthenticationIsOpen,
  handleAddGearFromGearCloset,
  handleAddBinToPack,
  tableData,
  darkMode,
}) => {
  const [selectedItems, setSelectedItems] = React.useState([]);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [binAnchorEl, setBinAnchorEl] = React.useState(null);
  const [subMenuAnchorEl, setSubMenuAnchorEl] = React.useState(null);
  const [openSubMenu, setOpenSubMenu] = React.useState(null);
  const [moreToolsAnchorEl, setMoreToolsAnchorEl] = React.useState(null);
  const [searchText, setSearchText] = React.useState("");
  const [deleteDuplicateIsOpen, setDeleteDuplicateIsOpen] =
    React.useState(false);
  const [gearBins, setGearBins] = React.useState([]);
  const [gearRows, setGearRows] = React.useState([]);
  const [filteredGearRows, setFilteredGearRows] = React.useState([]);
  const [isBeingFilteredToBin, setIsBeingFilteredToBin] = React.useState(false);
  const [userPackList, setUserPackList] = React.useState([]);

  const handleSetSearchText = (e) => {
    setSearchText(e.target.value);
  };
  const debouncedChangeHandler = React.useCallback(
    debounce(handleSetSearchText, 300),
    []
  );

  const handleClick = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleSubMenuClick = (e, key) => {
    setOpenSubMenu(key);
    setSubMenuAnchorEl(e.currentTarget);
  };

  const handleMoreToolsClick = (e) => {
    setMoreToolsAnchorEl(e.currentTarget);
  };

  const handleMoreToolsClose = () => {
    setMoreToolsAnchorEl(null);
  };

  const handleAddToCategoryClose = () => {
    setAnchorEl(null);
    handleSubMenuClose();
  };

  const handleSubMenuClose = () => {
    setSubMenuAnchorEl(null);
    setOpenSubMenu(null);
  };

  const handleAddToCategory = (tableKey, categoryIndex) => {
    handleAddGearFromGearCloset(tableKey, categoryIndex, selectedItems);
    handleAddToCategoryClose();
  };

  const handleRemoveDuplicates = () => {
    setDeleteDuplicateIsOpen(true);
    handleMoreToolsClose();
  };

  const handleClose = () => {
    setSelectedItems([]);
    setGearClosetIsOpen(false);
  };

  const handleAddToBin = (e) => {
    setBinAnchorEl(e.currentTarget);
  };

  const handleAddToBinClose = () => {
    setBinAnchorEl(null);
  };

  const handleCreateBin = async () => {
    const selectedItemIds = selectedItems.map(({ _id }) => _id);
    const newBin = await createNewBin(selectedItemIds);
    newBin.contents = selectedItems;
    newBin.id = newBin._id;
    if (newBin) {
      const newGearBins = [...gearBins, newBin];
      setGearBins(newGearBins);
      handleAddToBinClose();
    }
  };

  const handleAddToExistingBin = async (index) => {
    const currentBins = [...gearBins];
    const newContents = [...gearBins[index].contents, ...selectedItems];
    const { finalContents } = newContents.reduce(
      (acc, curr) => {
        if (!acc.seenContents[curr.id]) {
          acc.finalContents.push(curr);
          acc.seenContents[curr.id] = true;
        }
        return acc;
      },
      {
        finalContents: [],
        seenContents: {},
      }
    );
    currentBins[index].contents = finalContents;
    setGearBins(currentBins);
    const selectedItemIds = finalContents.map(({ _id }) => _id);
    const binId = currentBins[index].id;
    await updateUserBin(binId, { contents: selectedItemIds });
    handleAddToBinClose();
  };

  const handleDeleteFromBin = async (binIndex, itemId) => {
    const currentBins = [...gearBins];
    const newContents = gearBins[binIndex].contents.filter(
      ({ id }) => id !== itemId
    );
    currentBins[binIndex].contents = newContents;
    const binId = currentBins[binIndex].id;
    const newItemIds = newContents.map(({ _id }) => _id);
    await updateUserBin(binId, { contents: newItemIds });
    setGearBins(currentBins);
  };

  const handleDeleteBin = async (binId) => {
    const newBins = gearBins.filter((bin) => bin.id !== binId);
    await deleteUserBin(binId);
    setGearBins(newBins);
  };

  const handleUpdateBinText = (binIndex, key, text) => {
    const newBins = [...gearBins];
    newBins[binIndex][key] = text;
    setGearBins(newBins);
  };

  const handleFilterToBin = (binTitle, contents) => {
    const filteredIdSet = new Set(contents.map(({ id }) => id));
    const filteredToBin = gearRows.filter((gearRow) =>
      filteredIdSet.has(gearRow.id)
    );
    setFilteredGearRows(filteredToBin);
    setIsBeingFilteredToBin(binTitle);
  };

  const clearBinFilter = () => {
    setFilteredGearRows(gearRows);
    setIsBeingFilteredToBin(null);
  };

  React.useEffect(() => {
    (async () => {
      const usersGearBins = await getBins();
      if (usersGearBins) {
        const hydratedBins = usersGearBins.reduce((acc, curr) => {
          const bin = { ...curr };
          const idSet = new Set(bin.contents);
          const idGcClosetMap = {};
          gearRows.forEach((row) => {
            if (idSet.has(row.id)) {
              idGcClosetMap[row.id] = row;
            }
          });
          const hydratedContent = bin.contents
            .map((id) => idGcClosetMap[id])
            .filter((row) => row);
          acc.push({ id: bin._id, ...bin, contents: hydratedContent });
          return acc;
        }, []);
        setGearBins(hydratedBins);
      }
    })();
  }, [gearRows]);

  React.useEffect(() => {
    (async () => {
      const fetchedUserPackData = await getUserPackData();
      setUserPackList(fetchedUserPackData);
    })();
  }, []);

  return (
    <Container
      sx={{
        maxHeight: "100vh",
        marginTop: "20px",
      }}
    >
      <Button
        onClick={handleClose}
        startIcon={<Undo2 size={18} />}
        sx={TOOLBAR_BUTTON_SX}
      >
        Back to Pack
      </Button>
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between">
          <Typography
            variant="h4"
            sx={{
              fontFamily: HEADING_FONT,
              fontWeight: 800,
              letterSpacing: "-0.02em",
            }}
          >
            Gear Closet
          </Typography>
          <IconButton onClick={handleClose}>
            <X size={20} />
          </IconButton>
        </Stack>
        <Typography
          variant="subtitle1"
          sx={{
            color: getMutedText(darkMode),
            fontFamily: HEADING_FONT,
            maxWidth: "800px",
            paddingTop: "4px",
          }}
        >
          Here you can create, view and manage your gear, so you can add it to
          your pack faster.
        </Typography>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={1}>
            <Button
              onClick={handleClick}
              endIcon={<ChevronDown size={16} />}
              variant="contained"
              disableElevation
              size="small"
              sx={TOOLBAR_BUTTON_SX}
              disabled={selectedItems.length === 0 || userPackList.length === 0}
            >
              {"Add to Pack"}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={anchorEl}
              onClose={handleAddToCategoryClose}
              PaperProps={{ sx: { borderRadius: "12px" } }}
            >
              {Object.keys(userPackList).map((key) => (
                <>
                  <MenuItem onClick={(e) => handleSubMenuClick(e, key)}>
                    {userPackList[key].packName}
                    <ChevronRight size={16} />
                  </MenuItem>
                  <Menu
                    key={key}
                    anchorEl={subMenuAnchorEl}
                    open={openSubMenu === key}
                    onClose={handleSubMenuClose}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    {userPackList[key].categories.map((title, index) => (
                      <MenuItem
                        key={`${key}-${index}`}
                        onClick={() => {
                          handleAddToCategory(key, index);
                        }}
                      >
                        {title}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              ))}
            </Menu>
            <Button
              onClick={handleAddToBin}
              endIcon={<ChevronDown size={16} />}
              variant="contained"
              disableElevation
              size="small"
              sx={TOOLBAR_BUTTON_SX}
              disabled={selectedItems.length === 0 || tableData.length === 0}
            >
              {"Add to Bin"}
            </Button>
            <Menu
              anchorEl={binAnchorEl}
              open={Boolean(binAnchorEl)}
              onClose={handleAddToBinClose}
              PaperProps={{ sx: { borderRadius: "12px" } }}
            >
              <MenuItem
                key={`create-bin-menu-item`}
                onClick={() => {
                  handleCreateBin();
                }}
              >
                <ListItemIcon>
                  <Plus size={18} />
                </ListItemIcon>
                <ListItemText>Create New Bin</ListItemText>
              </MenuItem>
              {gearBins.map((bin, index) => (
                <MenuItem
                  key={`${bin.title}-${index}`}
                  onClick={() => {
                    handleAddToExistingBin(index);
                  }}
                >
                  <ListItemIcon></ListItemIcon>
                  <ListItemText>{bin.title}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
            <Button
              onClick={handleMoreToolsClick}
              endIcon={<ChevronDown size={16} />}
              variant="contained"
              disableElevation
              size="small"
              sx={TOOLBAR_BUTTON_SX}
            >
              {"More Actions"}
            </Button>
            <Menu
              anchorEl={moreToolsAnchorEl}
              open={Boolean(moreToolsAnchorEl)}
              onClose={handleMoreToolsClose}
              PaperProps={{ sx: { borderRadius: "12px" } }}
            >
              <MenuItem
                key="remove-duplicates"
                onClick={handleRemoveDuplicates}
              >
                <ListItemIcon>
                  <Eraser size={18} />
                </ListItemIcon>
                <ListItemText primary="Remove Duplicates From Gear Closet" />
              </MenuItem>
            </Menu>
          </Stack>
          <TextField
            size={"small"}
            sx={{
              width: "300px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "9px",
                fontFamily: HEADING_FONT,
              },
            }}
            placeholder="Search"
            onChange={debouncedChangeHandler}
          ></TextField>
        </Stack>
        <GearClosetDataTable
          handleSetAuthenticationIsOpen={handleSetAuthenticationIsOpen}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          darkMode={darkMode}
          searchText={searchText}
          deleteDuplicateIsOpen={deleteDuplicateIsOpen}
          setDeleteDuplicateIsOpen={setDeleteDuplicateIsOpen}
          gearRows={gearRows}
          setGearRows={setGearRows}
          filteredGearRows={filteredGearRows}
          setFilteredGearRows={setFilteredGearRows}
        />
        {isBeingFilteredToBin ? (
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: HEADING_FONT,
              color: getAccent(darkMode),
            }}
          >
            {`Your Gear Closet is Currently being filtered to the contents of ${isBeingFilteredToBin}. `}
            <Link
              sx={{
                fontWeight: 600,
                "&:hover": { cursor: "pointer" },
              }}
              onClick={clearBinFilter}
            >
              Click to Remove Filter
            </Link>
          </Typography>
        ) : (
          <></>
        )}
        <GearBinContainer
          gearBins={gearBins}
          handleDeleteFromBin={handleDeleteFromBin}
          handleDeleteBin={handleDeleteBin}
          handleFilterToBin={handleFilterToBin}
          handleAddBinToPack={handleAddBinToPack}
          darkMode={darkMode}
          handleUpdateBinText={handleUpdateBinText}
        />
      </Stack>
    </Container>
  );
};

export default GearCloset;
